/** Header, footer and navigation shared by the public pages (home, sign-in, legal). */
import { useEffect, useState } from "react";
import { APP_NAME } from "@brainlog/shared";
import { Button } from "@/components/ui/button";
import { useMe } from "@/lib/queries";
import { openCookieSettings } from "@/lib/analytics";
import { LINKEDIN_PAGE, YOUTUBE_CHANNEL } from "./content";

/** Move to another public page without reloading. */
export function go(path: string) {
  if (path === window.location.pathname + window.location.search) return;
  window.history.pushState({}, "", path);
  window.dispatchEvent(new PopStateEvent("popstate"));
  const hash = path.split("#")[1];
  if (hash) requestAnimationFrame(() => document.getElementById(hash)?.scrollIntoView({ behavior: "smooth" }));
  else window.scrollTo({ top: 0 });
}

export function usePathname() {
  const [path, setPath] = useState(window.location.pathname);
  useEffect(() => {
    const on = () => setPath(window.location.pathname);
    window.addEventListener("popstate", on);
    return () => window.removeEventListener("popstate", on);
  }, []);
  return path;
}

function Logo() {
  return (
    <a
      href="/"
      onClick={(e) => {
        e.preventDefault();
        go("/");
      }}
      className="flex items-center gap-2 font-display text-lg font-semibold"
    >
      <img src="/icon-192.png" alt="" aria-hidden className="size-8" />
      {APP_NAME}
    </a>
  );
}

const section = (id: string) => (e: React.MouseEvent) => {
  e.preventDefault();
  if (window.location.pathname !== "/") go(`/#${id}`);
  else document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
};

export function PublicHeader() {
  const me = useMe();
  return (
    <header className="sticky top-0 z-20 border-b bg-background/85 backdrop-blur">
      <div className="mx-auto flex h-14 max-w-6xl items-center gap-4 px-5">
        <Logo />
        <nav aria-label="Main" className="ml-4 hidden flex-1 gap-5 text-sm text-muted-foreground md:flex">
          <a href="/#features" onClick={section("features")} className="hover:text-foreground">
            Features
          </a>
          <a href="/#pricing" onClick={section("pricing")} className="hover:text-foreground">
            Pricing
          </a>
          <a href="/#questions" onClick={section("questions")} className="hover:text-foreground">
            Questions
          </a>
        </nav>
        <div className="ml-auto flex gap-2">
          {me.data ? (
            <Button size="sm" onClick={() => window.location.assign("/")}>
              Open {APP_NAME}
            </Button>
          ) : (
            <>
              <Button variant="ghost" size="sm" onClick={() => go("/login")}>
                Sign in
              </Button>
              <Button size="sm" onClick={() => go("/signup")}>
                Start free
              </Button>
            </>
          )}
        </div>
      </div>
    </header>
  );
}

export function PublicFooter() {
  const link = (path: string, label: string) => (
    <a
      href={path}
      onClick={(e) => {
        e.preventDefault();
        go(path);
      }}
      className="hover:text-foreground"
    >
      {label}
    </a>
  );
  return (
    <footer className="border-t py-8 text-sm text-muted-foreground">
      <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-x-6 gap-y-2 px-5">
        <span>
          © {new Date().getFullYear()} {APP_NAME}
        </span>
        {link("/#pricing", "Pricing")}
        {link("/privacy", "Privacy")}
        {link("/terms", "Terms")}
        <button type="button" onClick={openCookieSettings} className="hover:text-foreground">
          Cookie settings
        </button>
        <a href={YOUTUBE_CHANNEL} target="_blank" rel="noopener" className="hover:text-foreground">
          YouTube
        </a>
        <a href={LINKEDIN_PAGE} target="_blank" rel="noopener" className="hover:text-foreground">
          LinkedIn
        </a>
        <a href="mailto:contact@brainlog.net" className="hover:text-foreground">
          contact@brainlog.net
        </a>
      </div>
    </footer>
  );
}

/** The centred card used by the sign-in pages. */
export function AuthCard({ title, subtitle, children }: { title: string; subtitle?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="flex min-h-full flex-col bg-muted/30">
      <div className="mx-auto flex w-full max-w-6xl px-5 py-4">
        <Logo />
      </div>
      <main className="flex flex-1 items-start justify-center px-4 pb-16 pt-6 sm:pt-12">
        <div className="w-full max-w-md rounded-2xl border bg-card p-6 shadow-sm sm:p-8">
          <h1 className="font-display text-2xl font-semibold">{title}</h1>
          {subtitle && <div className="mt-1 text-sm text-muted-foreground">{subtitle}</div>}
          <div className="mt-6">{children}</div>
        </div>
      </main>
    </div>
  );
}
